import { isInChina } from '@fshr/shared-react';

export type YouTubePlayer = {
	playVideo: () => void;
	pauseVideo: () => void;
	getPlayerState?: () => number;
	destroy: () => void;
};

export type YouTubeApi = {
	Player: new (element: HTMLElement, options: Record<string, unknown>) => YouTubePlayer;
	ready: (fn: () => void) => void;
};

declare global {
	interface Window {
		YT?: YouTubeApi;
		onYouTubeIframeAPIReady?: () => void;
	}
}

// YT.PlayerState.PLAYING and .BUFFERING. Only these two are worth pausing.
const PLAYING_STATES = new Set([1, 3]);

export const isPlaying = (player: YouTubePlayer): boolean =>
	player.getPlayerState ? PLAYING_STATES.has(player.getPlayerState()) : false;

type ReadyCallback = (yt: YouTubeApi) => void;

const API_SRC = 'https://www.youtube.com/iframe_api';
const POLL_MS = 100;
const POLL_LIMIT = 100;

const queued: ReadyCallback[] = [];
let loading = false;

function drain(): boolean {
	const yt = window.YT;
	if (!yt?.ready) return false;
	queued.splice(0).forEach(queuedFn => yt.ready(() => queuedFn(yt)));
	return true;
}

/**
 * Runs `fn` once the YouTube IFrame API is available, handing it the API. The API
 * is a single global script, so injection and the pending queue live at module
 * scope rather than per component. Mirrors
 * `design3/asset-build/js/custom/youtube.js` in fs-blacklight.
 *
 * Both modules can be live on the same Blacklight page and the API calls only the
 * last `window.onYouTubeIframeAPIReady` it finds, so that callback is chained
 * rather than replaced, the script is injected only if nobody else already did,
 * and a bounded poll drains the queue if a later module takes the global back.
 *
 * In mainland China YouTube is unreachable, so `fn` never runs and the caller is
 * left with no player — same guard Blacklight applies before injecting the script.
 */
export function ytReady(fn: ReadyCallback): void {
	if (isInChina()) return;

	const loaded = window.YT;
	if (loaded?.ready) {
		loaded.ready(() => fn(loaded));
		return;
	}

	queued.push(fn);
	if (loading) return;
	loading = true;

	const previous = window.onYouTubeIframeAPIReady;
	window.onYouTubeIframeAPIReady = () => {
		previous?.();
		drain();
	};

	if (!document.querySelector(`script[src^="${API_SRC}"]`)) {
		const tag = document.createElement('script');
		tag.src = API_SRC;
		document.head.appendChild(tag);
	}

	let attempts = 0;
	const timer = window.setInterval(() => {
		attempts += 1;
		if (drain() || attempts >= POLL_LIMIT) {
			window.clearInterval(timer);
		}
	}, POLL_MS);
}
